// Hook for handling clipboard operations (copy, cut, paste, delete, save) in React Flow diagrams
import { useCallback, useEffect } from "react";
import { useDiagram } from "@/context/diagram-context";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/lib/constants";

export interface UseReactFlowClipboardOptions {
  enabled?: boolean;
  onSave?: () => void | Promise<void>; // Optional save handler
}

/**
 * Hook to handle keyboard shortcuts for copy, cut, paste, delete, and save operations in React Flow
 * Integrates with the diagram context to manage shape operations
 *
 * Keyboard shortcuts:
 * - Copy: Ctrl+C / Cmd+C
 * - Cut: Ctrl+X / Cmd+X
 * - Paste: Ctrl+V / Cmd+V
 * - Delete: Delete / Backspace
 * - Save: Ctrl+S / Cmd+S
 * - Cancel Cut: Escape
 */
export const useReactFlowClipboard = (
  options: UseReactFlowClipboardOptions = {}
) => {
  const { enabled = true, onSave } = options;
  const diagram = useDiagram();

  // Handle copy operation
  const handleCopy = useCallback(() => {
    if (!diagram.selectedShape) {
      toast.info("Please select a shape to copy", {
        duration: TOAST_DURATION,
      });
      return;
    }

    // Find the component to get its name for the toast
    const component = diagram.diagramComponents.find(
      (c) => c.id === diagram.selectedShape
    );

    if (!component) {
      console.error("Component not found:", diagram.selectedShape);
      toast.error("Cannot copy: component not found", {
        duration: TOAST_DURATION,
      });
      return;
    }

    const componentName =
      component?.metadata?.name || component?.shape || "Component";
    const componentType = component?.type || "shape";

    // Check if this is a layer - layers might need special handling
    if (componentType === "layer") {
      console.warn("Attempting to copy a layer:", component);
    }

    diagram.copy3DShape(diagram.selectedShape);

    // Verify the shape was marked as cut

    toast.success(`Copied: ${componentName}`, {
      duration: TOAST_DURATION,
    });
  }, [diagram]);

  // Handle cut operation
  const handleCut = useCallback(() => {
    if (!diagram.selectedShape) {
      return;
    }

    // Find the component to get its name for the toast
    const component = diagram.diagramComponents.find(
      (c) => c.id === diagram.selectedShape
    );
    const componentName =
      component?.metadata?.name || component?.shape || "Component";

    diagram.cut3DShape(diagram.selectedShape);
    toast.success(`Cut: ${componentName}`, {
      duration: TOAST_DURATION,
    });
  }, [diagram]);

  // Handle paste operation
  const handlePaste = useCallback(() => {
    // Debug: Log current state

    // Check if there's a cut/copied shape
    const cutShape = diagram.diagramComponents.find((c) => c.cut);

    if (!cutShape) {
      toast.error("Nothing to paste. Please copy or cut a shape first.", {
        duration: TOAST_DURATION,
      });
      return;
    }

    if (!diagram.selectedShape) {
      toast.error("Please select a target shape to paste onto", {
        duration: TOAST_DURATION,
      });
      return;
    }

    // Don't allow pasting onto the same shape that was cut/copied
    if (diagram.selectedShape === cutShape.id) {
      toast.error("Cannot paste onto the same shape", {
        duration: TOAST_DURATION,
      });
      return;
    }

    const componentName =
      cutShape?.metadata?.name || cutShape?.shape || "Component";

    diagram.paste3DShape(cutShape.id);

    const action = diagram.isCopied ? "Pasted copy" : "Pasted";
    toast.success(`${action}: ${componentName}`, {
      duration: TOAST_DURATION,
    });
  }, [diagram]);

  // Handle delete operation
  const handleDelete = useCallback(() => {
    if (!diagram.selectedShape) {
      return;
    }

    // Find the component to get its name for the toast
    const component = diagram.diagramComponents.find(
      (c) => c.id === diagram.selectedShape
    );
    const componentName =
      component?.metadata?.name || component?.shape || "Component";

    diagram.remove3DShape(diagram.selectedShape);
    toast.success(`Deleted: ${componentName}`, {
      duration: TOAST_DURATION,
    });
  }, [diagram]);

  // Handle escape to cancel cut
  const handleEscape = useCallback(() => {
    const cutShape = diagram.diagramComponents.find((c) => c.cut);
    if (cutShape) {
      diagram.cancelCut3DShape(cutShape.id);
      toast.info("Cancelled cut operation", {
        duration: TOAST_DURATION,
      });
    }
  }, [diagram]);

  // Handle save operation
  const handleSave = useCallback(() => {
    if (!onSave) {
      toast.info("Save shortcut: Ctrl+S / Cmd+S", {
        duration: TOAST_DURATION,
      });
      return;
    }

    // Just trigger the callback - let the parent handle save modal/logic
    onSave();
  }, [onSave]);

  // Keyboard event handler
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      // Detect Mac vs Windows/Linux for proper modifier key
      const isMac = navigator.userAgent.toUpperCase().indexOf("MAC") >= 0;
      const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;

      // Copy: Ctrl+C or Cmd+C
      if (isCtrlOrCmd && event.key === "c") {
        event.preventDefault();
        handleCopy();
        return;
      }

      // Cut: Ctrl+X or Cmd+X
      if (isCtrlOrCmd && event.key === "x") {
        event.preventDefault();
        handleCut();
        return;
      }

      // Paste: Ctrl+V or Cmd+V
      if (isCtrlOrCmd && event.key === "v") {
        event.preventDefault();
        handlePaste();
        return;
      }

      // Save: Ctrl+S or Cmd+S
      if (isCtrlOrCmd && event.key === "s") {
        event.preventDefault();
        handleSave();
        return;
      }

      // Delete: Delete or Backspace key
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        handleDelete();
        return;
      }

      // Escape: Cancel cut operation
      if (event.key === "Escape") {
        handleEscape();
        return;
      }
    },
    [handleCopy, handleCut, handlePaste, handleSave, handleDelete, handleEscape]
  );

  // Set up keyboard event listener
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  // Return methods for programmatic access
  return {
    copy: handleCopy,
    cut: handleCut,
    paste: handlePaste,
    save: handleSave,
    delete: handleDelete,
    cancelCut: handleEscape,
  };
};
